/* Floating buy bar behaviour (mobile only), aligned with the ASHEIS storefront.
 *
 * The bar is visible from page load via CSS; this script fades it out as the
 * product section scrolls away, so it stops covering content once the shopper
 * has moved past the product itself.
 *
 * The fade is anchored to the BOTTOM of .shopify-section--main-product rather
 * than to the top of the following section. The following section is often
 * populated by other scripts (recently viewed products reads localStorage,
 * related products fetches recommendations), so at DOMContentLoaded it can
 * still have no layout at all — getBoundingClientRect() then returns zeros,
 * which read as "already scrolled past" and left the bar stuck at opacity 0.
 * The main product section always has layout, so it cannot mis-measure.
 *
 * It also publishes how much of the bottom of the viewport the bar covers, as
 * --floating-buy-bar-offset plus the has-floating-buy-bar class on <html>, so
 * things other scripts pin to that edge can sit above it instead of on top of
 * it. Klaviyo's sign-up teaser is the one that needs it; see
 * sticky-buy-bar.css.
 */
(function () {
  function init() {
    var main = document.querySelector('.shopify-section--main-product');
    if (!main || !document.querySelector('.product-sticky-bar')) return;

    var mqMobile = window.matchMedia('(max-width: 699px)');
    var root = document.documentElement;

    function setOffset(px) {
      if (px > 0) {
        root.style.setProperty('--floating-buy-bar-offset', px + 'px');
        root.classList.add('has-floating-buy-bar');
      } else {
        root.style.removeProperty('--floating-buy-bar-offset');
        root.classList.remove('has-floating-buy-bar');
      }
    }

    function update() {
      // Look the bar up on every pass. Choosing another colour or size
      // re-renders it as a new element, so a reference kept from page load
      // points at the detached original: the new bar then never faded, and its
      // height would read as 0.
      var bar = document.querySelector('.product-sticky-bar');
      if (!bar) {
        setOffset(0);
        return;
      }

      // Only the mobile floating bar fades; >=700px keeps the stock behaviour.
      if (!mqMobile.matches) {
        bar.style.removeProperty('opacity');
        bar.style.removeProperty('pointer-events');
        setOffset(0);
        return;
      }

      var rect = main.getBoundingClientRect();
      var opacity;

      if (rect.width === 0 && rect.height === 0) {
        // Defensive: if the section somehow has no layout yet, leave the bar
        // visible rather than guessing it has been scrolled past.
        opacity = 1;
      } else {
        // rect.bottom shrinks as the product scrolls away. Stay fully visible
        // while the product still fills the lower half of the screen, then fade
        // to zero as its bottom edge reaches the top of the viewport.
        var fadeStart = window.innerHeight / 2;
        if (rect.bottom >= fadeStart) opacity = 1;
        else if (rect.bottom <= 0) opacity = 0;
        else opacity = rect.bottom / fadeStart;
      }

      bar.style.opacity = opacity.toFixed(3);
      bar.style.pointerEvents = opacity < 0.05 ? 'none' : 'auto';

      // Scaled by the fade, so whatever sits on the bar slides back down to the
      // corner as the bar disappears rather than jumping once it has gone.
      setOffset(Math.round(bar.offsetHeight * opacity));
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Images and app-injected sections change the layout after DOMContentLoaded,
    // so recompute once everything has settled instead of trusting the first pass.
    window.addEventListener('load', onScroll);
    if (window.ResizeObserver) new ResizeObserver(onScroll).observe(main);
    // The bar lives inside the product section and is replaced on every variant
    // change. Recompute when that happens rather than waiting for the next
    // scroll, or the new bar shows at full opacity wherever the page is.
    if (window.MutationObserver) new MutationObserver(onScroll).observe(main, { childList: true, subtree: true });

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
